// src/components/MyDeckPositions.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import "../App.css";

export default function MyDeckPositions({ user }) {
  const [decks, setDecks] = useState([]);
  const [cardData, setCardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDeck, setEditingDeck] = useState(null);
  const [newPosition, setNewPosition] = useState("");

  // 🟢 Fetch decks & card data
  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        const [deckRes, cardRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}api/data/snapDecks/user/${user.id}`),
          axios.get(`${process.env.REACT_APP_API_URL}api/data/snapAllCards`),
        ]);

        const decksSorted = (deckRes.data || []).sort(
          (a, b) => (a.deckposition ?? 9999) - (b.deckposition ?? 9999)
        );
        setDecks(decksSorted);
        setCardData(cardRes.data || []);
      } catch (err) {
        console.error("Failed to fetch decks/cards:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // 🟠 Save updated position
  const handleSave = async () => {
    if (!editingDeck || !newPosition.trim()) return;
    const deckId = editingDeck.id;
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}api/updateSnapDeckPosition/${deckId}`,
        { deckPosition: parseInt(newPosition) }
      );

      setDecks((prev) =>
        [...prev]
          .map((d) =>
            d.id === deckId ? { ...d, deckposition: parseInt(newPosition) } : d
          )
          .sort((a, b) => (a.deckposition ?? 9999) - (b.deckposition ?? 9999))
      );
      setEditingDeck(null);
      setNewPosition("");
    } catch (err) {
      console.error("Failed to update deck position:", err);
      alert("Failed to update deck position. Check console for details.");
    }
  };

  // 🎨 Tier Styles
  const getTierStyle = (position) => {
    if (position <= 3)
      return {
        border: "2px solid gold",
        background: "rgba(255,215,0,0.08)",
        boxShadow: "0 0 20px rgba(255,215,0,0.4)",
      };
    if (position <= 10)
      return {
        border: "2px solid silver",
        background: "rgba(192,192,192,0.08)",
        boxShadow: "0 0 10px rgba(192,192,192,0.3)",
      };
    if (position <= 25)
      return {
        border: "2px solid #cd7f32",
        background: "rgba(205,127,50,0.08)",
        boxShadow: "0 0 10px rgba(205,127,50,0.3)",
      };
    return {
      border: "1px solid #333",
      background: "rgba(255,255,255,0.02)",
    };
  };

  // 🎴 Get first 3 cards in deck
  const getDeckCards = async (deckId) => {
    try {
        const res = await axios.get(
        `${process.env.REACT_APP_API_URL}api/data/deckTopCards/${deckId}/${user.id}`
        );
        return res.data || [];
    } catch (err) {
        console.error("Failed to fetch top cards for deck:", err);
        return [];
    }
    };

  if (loading)
    return (
      <p style={{ textAlign: "center", color: "#ccc", marginTop: "3rem" }}>
        Loading deck positions…
      </p>
    );

  if (decks.length === 0)
    return (
      <p style={{ textAlign: "center", color: "#aaa", marginTop: "3rem" }}>
        You have no decks ranked yet.
      </p>
    );

  return (
    <div className="list-container">
      <h1 className="main-title" style={{ textAlign: "center" }}>
        My Deck Rankings
      </h1>

      <div
        className="tier-grid"
        style={{
          padding: "0 1.5rem",
          marginTop: "2rem",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: "1.25rem",
        }}
      >
        {decks.map((deck) => {
          const deckCards = getDeckCards(deck);
          return (
            <div
              key={deck.id}
              style={{
                ...getTierStyle(deck.deckposition),
                borderRadius: "12px",
                padding: "14px 12px 18px",
                textAlign: "center",
                backdropFilter: "blur(5px)",
                transition: "transform 0.25s ease",
              }}
            >
              <h3 style={{ color: "#fff", marginBottom: "6px", fontSize: "1.05rem" }}>
                #{deck.deckposition ?? "—"} — {deck.deckname}
              </h3>

              {/* 🎴 Mini Fan */}
                <div
                className="preview-fan"
                style={{
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "140px",
                    gap: "12px",
                    margin: "10px auto 6px",
                    transform: "translateY(-4px)",
                }}
                >
                {deckCards.length > 0 ? (
                    deckCards.map((c, i) => (
                    <img
                        key={c.id}
                        src={c.cardhtml}
                        alt={c.cardname}
                        title={c.cardname}
                        style={{
                        width: "85px",
                        height: "120px",
                        borderRadius: "6px",
                        transform: `rotate(${(i - 1) * 10}deg)`,
                        boxShadow: "0 4px 10px rgba(0,0,0,0.6)",
                        objectFit: "cover",
                        transition: "all 0.25s ease",
                        }}
                        onMouseEnter={(e) => {
                        e.currentTarget.style.transform = `scale(1.08) rotate(0deg)`;
                        e.currentTarget.style.zIndex = 5;
                        }}
                        onMouseLeave={(e) => {
                        e.currentTarget.style.transform = `rotate(${(i - 1) * 10}deg)`;
                        e.currentTarget.style.zIndex = 1;
                        }}
                    />
                    ))
                ) : (
                    <p style={{ color: "#888", fontSize: "0.85rem" }}>No cards found</p>
                )}
                </div>

              <p style={{ color: "#ccc", fontSize: "0.88rem", marginBottom: "6px" }}>
                Rank: {deck.deckrank || "Neutral"} <br />
                Version: {deck.versionnum || "-"} <br />
                Month: {deck.monthcompleted || "-"}
              </p>

              <button
                className="button"
                style={{
                  marginTop: "8px",
                  padding: "4px 10px",
                  fontSize: "0.85rem",
                }}
                onClick={() => {
                  setEditingDeck(deck);
                  setNewPosition(deck.deckposition ?? "");
                }}
              >
                Edit Rank
              </button>
            </div>
          );
        })}
      </div>

      {/* 🟡 Edit Rank Modal */}
      {editingDeck && (
        <div className="modal-overlay" onClick={() => setEditingDeck(null)}>
          <div
            className="modern-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "400px" }}
          >
            <button className="modal-close-btn" onClick={() => setEditingDeck(null)}>
              ✕
            </button>
            <h2
              style={{
                color: "rgb(197,176,55)",
                textAlign: "center",
                marginBottom: "1rem",
              }}
            >
              Update Deck Position
            </h2>
            <p style={{ color: "#ccc", textAlign: "center" }}>{editingDeck.deckname}</p>
            <input
              type="number"
              min="1"
              value={newPosition}
              onChange={(e) => setNewPosition(e.target.value)}
              placeholder="Enter new position..."
              className="modern-input"
              style={{ marginTop: "1rem" }}
            />
            <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
              <button className="modern-btn" onClick={handleSave}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
